import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, Eye, EyeOff, Building2 } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-slate-900 flex-col justify-between p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/10">
              <Building2 className="w-5 h-5 text-indigo-300" />
            </div>
            <span className="text-white/90 font-semibold text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Panelart Decor Pvt Ltd
            </span>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Expense Management,<br />
            <span className="text-indigo-300">Simplified.</span>
          </h1>
          <p className="text-slate-400 text-base leading-relaxed max-w-md">
            Submit, track, and manage expense reimbursements across all brands and divisions. Built for the team at Panelart Decor.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/5">
              <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Fast</div>
              <div className="text-xs text-slate-400 mt-1">Submit in seconds</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/5">
              <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Secure</div>
              <div className="text-xs text-slate-400 mt-1">Role-based access</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/5">
              <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Clear</div>
              <div className="text-xs text-slate-400 mt-1">Real-time tracking</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">
          Internal Expense Management System
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Panelart Decor Pvt Ltd</span>
            </div>
            <p className="text-sm text-gray-500">Internal Expense Management System</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sign in
              </h2>
              <p className="text-sm text-gray-500 mt-1">Enter your credentials to access the portal</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@company.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-10"
                    placeholder="Enter your password"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full justify-center"
                data-testid="login-button"
              >
                <LogIn className="w-4 h-4" />
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            </form>

            {showForgotPassword && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="forgot-password-message">
                <p className="text-sm text-amber-800 font-medium mb-1">Password Reset</p>
                <p className="text-sm text-amber-700">Please contact your administrator to reset your password.</p>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="mt-2 text-xs text-amber-600 hover:text-amber-800 font-medium"
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Panelart Decor Pvt Ltd &middot; Expense Portal
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
